import type { Framing } from '../components/StudioImage'

/**
 * The kinds of class the public page is built from (D-191).
 *
 * Everything here is genuinely Tonya's. The import derives a kind and files
 * classes under it so the page works on day one, then never writes to one
 * again — so unlike a class (D-124), nothing edited here is undone by dropping
 * the exports in.
 *
 * <b>Marketing metadata only.</b> Membership lives on the class, and the
 * classes screen already holds the whole list — nesting it here too would ship
 * the same rows twice and give the screen two places to disagree about which
 * kind a class is in (D-205).
 */
export type ClassTypeView = {
  classTypeId: string
  name: string
  blurb: string | null
  heroMediaAssetId: string | null
  heroUrl: string | null
  /** How the photo sits in the wide banner on the public page (D-199). */
  heroFraming: Framing | null
  sortOrder: number
  /** Null once made by hand — nothing in Jackrabbit corresponds to it. */
  derivedKey: string | null
  /**
   * Which programme this kind is for, or null for unsaid (D-227).
   *
   * <b>A label, not a policy.</b> Attendance, capacity and self-registration
   * read the *class* programme and only that — a class in a kind marked
   * Competitive is still recreational if its own programme says so.
   */
  studioProgramId: string | null
  programName: string | null
  /** Classes filed here whose own programme disagrees. Reported, never fixed. */
  classesInAnotherProgram: number
  /**
   * Whether this kind's classes may reach the public page at all (D-261).
   *
   * <b>ANDed with the switch on each class, not a replacement for it.</b> A
   * class shows when its own flag is on and this gate is open — so a ticked
   * class under a shut gate is invisible, and the class panel has to say so.
   */
  showInPublicCatalog: boolean
  /**
   * Whether a person chose that, rather than it being the import's default.
   *
   * "Off because it is competitive" and "off because Tonya turned it off" look
   * identical on screen and mean different things to whoever decides next.
   */
  visibilityIsOurs: boolean
  /**
   * Whether this kind's programme appears publicly at all, or null when the
   * kind has named no programme (D-261).
   *
   * <b>So the screen can say why a gate is shut without matching on a name.</b>
   * Programme names are editable reference data (NFR-14), and
   * `name === 'Competitive'` is a rule that stops working silently the day
   * somebody renames it.
   */
  programmeIsPublic: boolean | null
}

export type ClassTypeEdit = {
  name: string
  blurb: string | null
  heroMediaAssetId: string | null
  sortOrder: number
  /** Null clears it back to unsaid, which is a real answer. */
  studioProgramId?: string | null
  /**
   * The gate (D-261). Omitted on a create means "use the programme's default" —
   * competitive shut, everything else open.
   *
   * Omitted on a save means "this form did not carry the field", never "shut
   * it": the switch has its own call, so an edit here cannot take a kind off
   * the site as a side effect of a rename.
   */
  showInPublicCatalog?: boolean | null
}

/**
 * The id the picker's URL points at.
 *
 * The media picker hands back `/api/media/{id}` because that is what a content
 * block stores; a class type stores the id itself, so that the day the media
 * route changes shape a hundred rows do not need rewriting.
 */
export function assetIdFromUrl(url: string | null): string | null {
  if (!url) return null

  const match = /\/api\/media\/([0-9a-f-]{36})/i.exec(url)
  return match ? match[1] : null
}

/** The reverse, for handing the picker what is currently chosen. */
export function urlFromAssetId(assetId: string | null): string {
  return assetId ? `/api/media/${assetId}` : ''
}

export async function fetchClassTypes(): Promise<ClassTypeView[]> {
  const res = await fetch('/api/admin/class-types')
  if (!res.ok) throw new Error(`Could not load the class types (${res.status})`)
  return res.json()
}

async function send(url: string, method: string, body: unknown): Promise<ClassTypeView[]> {
  const res = await fetch(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.title ?? `Could not save that (${res.status})`)
  }

  return res.json()
}

export const createClassType = (edit: ClassTypeEdit) =>
  send('/api/admin/class-types', 'POST', edit)

export const saveClassType = (id: string, edit: ClassTypeEdit) =>
  send(`/api/admin/class-types/${id}`, 'PUT', edit)

export const assignClass = (classId: string, classTypeId: string | null) =>
  send('/api/admin/class-types/assign', 'POST', { classId, classTypeId })

/** Filing a whole selection at once — one call, so it cannot half-apply (D-205). */
export const assignClasses = (classIds: string[], classTypeId: string | null) =>
  send('/api/admin/class-types/assign-many', 'POST', { classIds, classTypeId })

/**
 * Show or hide every class in a kind at once (D-204).
 *
 * The bulk action that replaced the kind's own visibility flag. That flag read
 * as though it hid the programme and did not — the classes simply fell through
 * to "More classes" with the heading gone.
 */
export const setKindVisibility = (classTypeId: string, showInPublicCatalog: boolean) =>
  send('/api/admin/class-types/visibility', 'POST', { classTypeId, showInPublicCatalog })

/**
 * Opening or shutting the kind's own gate onto the public page (D-261).
 *
 * <b>Not the same as {@link setKindVisibility}, and the difference matters.</b>
 * That one edits every class in the kind; this one edits a single field on the
 * kind and leaves the classes untouched — so shutting the gate to test
 * something and opening it again gives back exactly the mix of ticked and
 * unticked classes that was there before.
 */
export const setKindGate = (classTypeId: string, showInPublicCatalog: boolean) =>
  send('/api/admin/class-types/gate', 'POST', { classTypeId, showInPublicCatalog })

/**
 * Thrown when a kind still holds classes and nobody has confirmed (D-216).
 *
 * Carried as its own type so the screen can tell "you need to confirm this"
 * apart from "that failed" — the first wants a question, the second an error.
 */
export class NeedsConfirmation extends Error {
  // A plain field, not a constructor parameter property: `erasableSyntaxOnly`
  // is on, so the shorthand is a compile error rather than a style choice.
  detail: string | null

  constructor(message: string, detail: string | null) {
    super(message)
    this.name = 'NeedsConfirmation'
    this.detail = detail
  }
}

/**
 * Removes a kind of class, unfiling whatever was in it (D-216).
 *
 * <b>Its classes are never deleted with it.</b> They keep their place on the
 * public page under "More classes" until they are filed somewhere else — losing
 * a class because somebody tidied up the catalog is far worse than an unheaded
 * row (D-191).
 */
export async function deleteClassType(id: string, confirm = false): Promise<ClassTypeView[]> {
  const res = await fetch(`/api/admin/class-types/${id}?confirm=${confirm}`, { method: 'DELETE' })

  if (res.status === 409) {
    const problem = await res.json().catch(() => null)
    throw new NeedsConfirmation(problem?.title ?? 'This still has classes in it', problem?.detail ?? null)
  }

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.title ?? `Could not remove that (${res.status})`)
  }

  return res.json()
}

/** The order after a drag, sent whole so no two kinds ever claim one slot (D-205). */
export const reorderClassTypes = (classTypeIds: string[]) =>
  send('/api/admin/class-types/order', 'POST', { classTypeIds })

/** Names in the order they appear on the public page, for grouping the list. */
export function kindNames(types: ClassTypeView[]): Map<string, string> {
  return new Map(types.map((t) => [t.classTypeId, t.name]))
}

/**
 * What is still missing before the public page looks finished.
 *
 * <b>Counted rather than left to be noticed.</b> A kind with no photograph
 * renders as a bare heading, which is not broken but is not the page Tonya
 * asked for — and the only way to spot it otherwise is to open the public site
 * and scroll.
 */
export function needsAttention(types: ClassTypeView[]): {
  withoutPhoto: number
  withoutBlurb: number
  withoutProgram: number
  disagreeing: number
} {
  return {
    withoutPhoto: types.filter((t) => !t.heroUrl).length,
    withoutBlurb: types.filter((t) => !t.blurb).length,

    /*
     * Unclassified, and holding a class that contradicts the classification
     * (D-227). The second is the one worth chasing: under D-51 a comp class
     * sitting in a rec kind usually means a missed dropdown in Jackrabbit, and
     * the only other way to notice is to read 185 rows.
     */
    withoutProgram: types.filter((t) => !t.studioProgramId).length,
    disagreeing: types.filter((t) => t.classesInAnotherProgram > 0).length,
  }
}

/**
 * Where an up or down arrow should land a kind (D-218).
 *
 * <b>Past the next kind you can see, not the next one that exists.</b> The
 * stored order holds every kind; the screen may be showing five of eleven
 * because a filter is on. Stepping by one position in the *stored* list moves a
 * kind past a neighbour that is not rendered — the request succeeds, the order
 * genuinely changes, and nothing appears to happen. That is precisely how this
 * was reported: arrows that "don't do anything", and a 200 in the network tab.
 *
 * Returns null when there is nothing visible to step past, so the arrow can be
 * disabled rather than firing a request that changes nothing.
 */
export function stepTarget(
  order: readonly string[],
  visible: readonly string[],
  id: string,
  direction: 'up' | 'down',
): number | null {
  const seen = visible.indexOf(id)
  if (seen < 0) return null

  const neighbour = visible[direction === 'up' ? seen - 1 : seen + 1]
  if (neighbour === undefined) return null

  const target = order.indexOf(neighbour)
  return target < 0 ? null : target
}

/**
 * Moving an item within a list, for drag-to-reorder.
 *
 * Pure, so the drag itself can be tested without a DOM — the arithmetic is
 * where reordering goes wrong, and it goes wrong in exactly one direction:
 * dragging downward, where removing the item first shifts every index after it.
 */
export function moved<T>(items: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || from >= items.length) return [...items]

  const next = [...items]
  const [item] = next.splice(from, 1)
  next.splice(Math.max(0, Math.min(next.length, to)), 0, item)

  return next
}
